import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Alert, AlertDescription } from "../components/ui/alert";
import { useToast } from "../hooks/use-toast";
import { resetProgress } from "../state/progress";
import { ShieldAlert, Settings as SettingsIcon } from "lucide-react";
import "../styles/hud.css";

export default function Settings() {
  const [confirm, setConfirm] = useState("");
  const nav = useNavigate();
  const { toast } = useToast();

  const canReset = useMemo(() => confirm.trim().toLowerCase() === "reset", [confirm]);

  const handleReset = () => {
    if (!canReset) return;
    // Clear all app local data
    resetProgress();
    try {
      localStorage.removeItem("nofap27_hero_started_v1");
      localStorage.removeItem("nofap27_weektasks_v1");
      localStorage.removeItem("nofap27_streak_v1");
      localStorage.removeItem("rl_tour_completed_v1");
      localStorage.removeItem("rl_site_updates_v1");
      localStorage.removeItem("rl_tracker_unlocked_weeks_v1");
      localStorage.removeItem("rl_diary_unlock_seen_v1");
      localStorage.removeItem("rl_time_offset_ms_v1"); // reset timewarp to actual local date
    } catch {}
    toast({ title: "All data reset", description: "Progress, daily tasks, streak, tutorial, hero state, tracker moderator unlocks, diary unlock message, site updates flag, and time offset have been cleared." });
    setConfirm("");
    nav("/");
  };

  const resetTutorialOnly = () => {
    try { localStorage.removeItem("rl_tour_completed_v1"); } catch {}
    toast({ title: "Tutorial reset", description: "The welcome tutorial will play again next time you open Home." });
  };

  const runTutorialNow = () => {
    try { localStorage.removeItem("rl_tour_completed_v1"); } catch {}
    nav("/");
    // Auto-start will run on Home since the key was cleared
  };

  return (
    <div className="mx-auto max-w-3xl px-4 py-10">
      <Card className="elevated-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-slate-900 dark:text-slate-100">
            <SettingsIcon className="h-5 w-5" /> Settings
          </CardTitle>

        </CardHeader>
        <CardContent>
          <div className="rounded-lg border border-slate-200 dark:border-slate-700 p-4">
            <div className="mb-6 grid gap-2 sm:grid-cols-[1fr_auto_auto] items-center">
              <div className="text-sm text-slate-700 dark:text-slate-300">Welcome tutorial</div>
              <Button variant="secondary" onClick={resetTutorialOnly}>Reset tutorial</Button>
              <Button className="btn-primary" onClick={runTutorialNow}>Run now</Button>
            </div>
            <div className="flex items-center gap-2 text-slate-900 dark:text-slate-100 font-medium">
              <ShieldAlert className="h-5 w-5 text-red-500" /> Reset Progress
            </div>
            <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">
              This permanently clears your locally stored progress and notes. It also resets the Home hero to its initial “Open Intro” state.
              Type <span className="font-semibold">reset</span> below to enable the button.
            </p>

            <div className="mt-4 grid gap-3 sm:grid-cols-[1fr_auto]">
              <Input
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                placeholder="Type reset to confirm"
                aria-label="Reset confirmation input"
              />
              <Button
                variant="destructive"
                disabled={!canReset}
                onClick={handleReset}
                aria-disabled={!canReset}
              >
                Reset now
              </Button>
            </div>

            {!canReset && (
              <Alert className="mt-3">
                <AlertDescription className="text-slate-600 dark:text-slate-300">
                  To protect your data, the reset button activates only when you type "reset" exactly.
                </AlertDescription>
              </Alert>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}